import { Autocomplete, Card, InputAdornment } from "@mui/material";
import Grid from "@mui/material/Grid2";
import cep from "cep-promise";
import { useFormikContext } from "formik";
import React, { useMemo, useRef } from "react";
import InputMaskWrapper from "@/components/atoms/InputMaskWrapper";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import MDInput from "@/components/atoms/MDInput";
import { OrderFormData } from "@/components/screens/OrderFormPage/types";
import { CEP_LENGHT, STATE_UF_TRANSLATIONS } from "@/constants/documents.constants";
import { removeNonIntegersFromString } from "@/helpers/string-helpers";
import useLoading from "@/hooks/use-loading";
import { OrderDataTabMessages } from "@/messages/order-form-page/order-data-tab.messages";
import useGetCoverageAreas from "@/hooks/requests/coverage-area/use-get-coverage-areas";
import { FilterType, IFilter } from "@/types/filter.types";
import ICoverageArea from "@/interfaces/coverage-area.interface";

type Props = {
  messages: OrderDataTabMessages;
};

export default function Delivery({ messages }: Props) {
  const shippingAddressNumberRef = useRef<HTMLInputElement>(null);
  const formik = useFormikContext<OrderFormData>();
  const { values, errors, setFieldValue, handleChange, setFieldError } = formik;

  const {
    startLoading,
    endLoading,
    isLoading: isLoadingCEP,
    loadingIcon: loadingIconCEP,
  } = useLoading({ loadingIconColor: "primary" });

  const emptyFilters = [
    { property: "areaDescription", value: "", filterType: FilterType.Contains },
  ] as IFilter<ICoverageArea>[];
  const { data: allAreasData } = useGetCoverageAreas({
    filters: emptyFilters,
    pageNumber: 0,
    pageSize: 1000,
  });

  const availableFreightTypes = useMemo(() => {
    const items = allAreasData?.items || [];
    const normalize = (s: string) => {
      const lower = s.trim().toLowerCase();
      if (!lower) return "";
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    };
    const set = new Set<string>();
    items.forEach((i) => {
      if (typeof i.freightType === "string" && i.freightType.trim()) {
        set.add(normalize(i.freightType));
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allAreasData]);
  const hasFreightTypeOptions = availableFreightTypes.length > 0;

  const clearData = () => {
    setFieldValue("shippingAddressState", "");
    setFieldValue("shippingAddressCity", "");
    setFieldValue("shippingAddressDistrict", "");
    setFieldValue("shippingAddress", "");
  };

  const handleCEPChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = e;

    const formattedValue = removeNonIntegersFromString(value);
    setFieldError("shippingAddressZipcode", undefined);
    setFieldValue("shippingAddressZipcode", formattedValue);
    const isCEPCompleted = formattedValue.length === CEP_LENGHT;
    if (!isCEPCompleted) clearData();
    if (isCEPCompleted) {
      startLoading();
      try {
        // Restringe provedores que funcionam com CORS no browser para evitar erros do apicep/widenet
        const cepData = await cep(formattedValue, { providers: ["viacep", "brasilapi"] });
        const addressState = STATE_UF_TRANSLATIONS[cepData.state];
        setFieldValue("shippingAddressState", addressState);

        setFieldValue("shippingAddressCity", cepData.city);
        setFieldValue("shippingAddressDistrict", cepData.neighborhood);
        setFieldValue("shippingAddress", cepData.street);
        setTimeout(() => {
          shippingAddressNumberRef.current?.focus();
        }, 0);
      } catch (err) {
        setFieldError("shippingAddressZipcode", messages.cepNotFoundError);
        clearData();
      }
      endLoading();
    }
  };

  return (
    <Card>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between">
          <MDTypography variant="h5" fontWeight="bold">
            {messages.groupDeliveryDataTitle}
          </MDTypography>
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <InputMaskWrapper
                mask="99999-999"
                value={values.shippingAddressZipcode || ""}
                onChange={handleCEPChange}
                disabled={values.disabledButton}
                variant="standard"
                name="shippingAddressZipcode"
                label={messages.shippingAddressZipcodeLabel}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  ...(isLoadingCEP && {
                    endAdornment: (
                      <InputAdornment position="end">{loadingIconCEP}</InputAdornment>
                    ),
                  }),
                }}
                error={!!errors.shippingAddressZipcode}
                helperText={errors.shippingAddressZipcode}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <FormField
                variant="standard"
                name="shippingAddress"
                label={messages.shippingAddressLabel}
                value={values.shippingAddress || ""}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                disabled={values.disabledButton}
                error={!!errors.shippingAddress}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <FormField
                variant="standard"
                name="shippingAddressNumber"
                label={messages.shippingAddressNumberLabel}
                value={values.shippingAddressNumber || ""}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputRef={shippingAddressNumberRef}
                disabled={values.disabledButton}
                error={!!errors.shippingAddressNumber}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <FormField
                variant="standard"
                name="shippingAddressCompl"
                label={messages.shippingAddressComplLabel}
                value={values.shippingAddressCompl || ""}
                disabled={values.disabledButton}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.shippingAddressCompl}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <FormField
                variant="standard"
                name="shippingAddressDistrict"
                label={messages.shippingAddressDistrictLabel}
                value={values.shippingAddressDistrict || ""}
                disabled={values.disabledButton}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.shippingAddressDistrict}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <FormField
                variant="standard"
                name="shippingAddressCity"
                label={messages.shippingAddressCityLabel}
                value={values.shippingAddressCity || ""}
                disabled={values.disabledButton}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.shippingAddressCity}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <FormField
                variant="standard"
                name="shippingAddressState"
                label={messages.shippingAddressStateLabel}
                value={values.shippingAddressState || ""}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.shippingAddressState}
                disabled={values.disabledButton}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <FormField
                variant="standard"
                name="shippingAddressCountry"
                label={messages.shippingAddressCountryLabel}
                value={values.shippingAddressCountry || ""}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.shippingAddressCountry}
                disabled={values.disabledButton}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              {hasFreightTypeOptions ? (
                <Autocomplete
                  freeSolo
                  options={availableFreightTypes}
                  value={values.shippingAgentService || ""}
                  disabled={values.disabledButton}
                  onInputChange={(_, newInput) => setFieldValue("shippingAgentService", newInput)}
                  onChange={(_, newValue) =>
                    setFieldValue("shippingAgentService", newValue || "")
                  }
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      variant="standard"
                      label={messages.shippingAgentServiceLabel}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      disabled={values.disabledButton}
                      error={!!errors.shippingAgentService}
                      helperText={errors.shippingAgentService}
                    />
                  )}
                />
              ) : (
                <FormField
                  fullWidth
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                  label={messages.shippingAgentServiceLabel}
                  name="shippingAgentService"
                  onChange={handleChange}
                  value={values.shippingAgentService || ""}
                  error={!!errors.shippingAgentService}
                  disabled={values.disabledButton}
                />
              )}
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </Card>
  );
}
